import { ChangeEventHandler, KeyboardEvent, useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { produce } from 'immer';
import { DraftDesignConfiguration } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { clamp } from '@/utils/math/clamp';
import { MM_PER_INCH } from '@/utils/math/consts';
import { isInches } from '@/utils/params';
import { getMaxScaleForConfiguration } from '@/model/configuration/getMaxScale';
import { isPixelDesign } from '@/model/design/isDesignType';
import { getPrintAreaSize } from '@/model/printArea/getPrintAreaSize';
import { getIsStickerByPrintType } from '@/model/printType/getIsStickerByPrintType';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import {
  selectActiveDraftConfiguration,
  selectConfigurations,
} from '@/redux/draft/getters/selectConfigurations';
import { StoreState } from '@/redux/types';
import { Slider } from '@/components/common/Slider';
import { setTransformationAction } from '@/components/draft/draftState/actions/setTransformationAction';
import { setWarningAction } from '@/components/draft/draftState/actions/setWarningAction';
import { DraftWarning } from '@/components/draft/draftState/types';
import { getLocalizedSize } from '@/components/draft/utils/getLocalizedSize';

export const STICKER_SIZE_ROUTE = '/sticker-size';
export const MIN_STICKER_SIZE = 10; // minimum size in mm

interface StickerSizeState {
  cid: number;
  scale: number;
  minScale: number;
  maxScale: number;
  baseSize: number;
  maxSize: number;
  designHeight: number;
  designWidth: number;
  height: string;
  width: string;
  dpiBound: boolean;
}

type StickerSizeAction = (draft: StickerSizeState) => void;

type StickerSizeReducer = (draft: StickerSizeState, action: StickerSizeAction) => StickerSizeState;

const reducer: StickerSizeReducer = (draft, action) => {
  return produce(draft, (writableDraft: StickerSizeState) => action(writableDraft));
};

const initState = (configuration: DraftDesignConfiguration): StickerSizeState => {
  const {
    cid,
    design,
    printArea,
    scale,
    size,
    printType: { id },
  } = configuration;
  const { height: designHeight, width: designWidth } = size;
  const baseSize = Math.min(designHeight, designWidth);
  const minScale = getIsStickerByPrintType(id) ? MIN_STICKER_SIZE / baseSize : 0;
  const maxScale = getMaxScaleForConfiguration(configuration);

  let dpiBound = false;
  const printAreaSize = getPrintAreaSize(printArea);
  if (
    isPixelDesign(design) &&
    (designWidth < printAreaSize.width || designHeight < printAreaSize.height)
  ) {
    // pixel designs cannot scale to the full sticker size
    dpiBound = true;
  }

  return {
    cid,
    scale,
    minScale,
    maxScale,
    baseSize,
    maxSize: Math.round(maxScale * baseSize),
    designHeight,
    designWidth,
    height: getLocalizedSize(designHeight * scale, false),
    width: getLocalizedSize(designWidth * scale, false),
    dpiBound,
  };
};

const setHeightAndWidth = (draft: StickerSizeState) => {
  const { designHeight, designWidth, scale } = draft;
  const height = getLocalizedSize(designHeight * scale, false);
  if (draft.height !== height) {
    draft.height = height;
  }

  const width = getLocalizedSize(designWidth * scale, false);
  if (draft.width !== width) {
    draft.width = width;
  }
};

const updateScale = (draft: StickerSizeState, newScale: number) => {
  const clampedScale = clamp(newScale, draft.maxScale, draft.minScale);
  if (!Number.isNaN(clampedScale) && clampedScale !== draft.scale) {
    draft.scale = clampedScale;
  }
};

const parseInput = (input: string) => {
  const value = parseFloat(input.replace(',', '.'));

  if (isInches()) {
    return value * MM_PER_INCH;
  }

  return value;
};

const setHeightAction = (height: string, force?: boolean) => (draft: StickerSizeState) => {
  draft.height = height;

  const { designHeight } = draft;
  const parsedHeight = parseInput(height);
  if (
    force ||
    (!Number.isNaN(parsedHeight) &&
      parsedHeight >= draft.minScale * designHeight &&
      parsedHeight <= draft.maxScale * designHeight)
  ) {
    updateScale(draft, parsedHeight / designHeight);
  }

  if (force) {
    setHeightAndWidth(draft);
  }
};

const setWidthAction = (width: string, force?: boolean) => (draft: StickerSizeState) => {
  draft.width = width;

  const { designWidth } = draft;
  const parsedWidth = parseInput(width);
  if (
    force ||
    (!Number.isNaN(parsedWidth) &&
      parsedWidth >= draft.minScale * designWidth &&
      parsedWidth <= draft.maxScale * designWidth)
  ) {
    updateScale(draft, parsedWidth / designWidth);
  }

  if (force) {
    setHeightAndWidth(draft);
  }
};

const setScaleAction = (scale: number) => (draft: StickerSizeState) => {
  updateScale(draft, scale);
  setHeightAndWidth(draft);
};

const switchState = (configuration: DraftDesignConfiguration) => (draft: StickerSizeState) => {
  Object.assign(draft, initState(configuration));
};

const trackDesignSizing = (isSticker: boolean, isSlider = false) => {
  if (isSticker) return;
  if (!isSlider) {
    trackFeature('design-sizing-panel-input');
  } else {
    trackFeature('design-sizing-panel-slider');
  }
};

export const useStickerSize = () => {
  const configuration = useSelector((state: StoreState) => {
    return (
      (selectActiveDraftConfiguration(state) as DraftDesignConfiguration) ??
      (selectConfigurations(state)[0] as DraftDesignConfiguration)
    );
  });
  const [state, dispatch] = useReducer<StickerSizeReducer, DraftDesignConfiguration>(
    reducer,
    configuration,
    initState
  );
  const isSticker = getIsStickerByPrintType(configuration.printType.id);

  // Switch the configuration state, if another design is selected with the StickerPanel open
  useEffect(() => {
    if (state.cid === configuration.cid) return;
    dispatch(switchState(configuration));
  }, [state, configuration]);

  const { scale, height, width, maxScale, maxSize } = state;

  const draftDispatch = useReduxDraftDispatch();

  useEffect(() => {
    if (configuration.scale !== scale) {
      const timer = window.setTimeout(() => {
        draftDispatch(setTransformationAction(configuration, { scale }));
      }, 150);
      return () => clearTimeout(timer);
    }
    // only create the timeout if the local scale is changing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  const warnDpiBound = (newScale: number) => {
    if (newScale >= maxScale && state.dpiBound) {
      draftDispatch(setWarningAction(String(configuration.cid), DraftWarning.DPIBound));
    }
  };
  const setHeight: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newHeight = event.target.value;
    dispatch(setHeightAction(newHeight));
  };

  const blurHeight = () => {
    trackDesignSizing(isSticker);
    warnDpiBound(parseInput(height) / state.designHeight);
    dispatch(setHeightAction(height, true));
  };

  const setWidth: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newWidth = event.target.value;
    dispatch(setWidthAction(newWidth));
  };

  const blurWidth = () => {
    trackDesignSizing(isSticker);
    warnDpiBound(parseInput(width) / state.designWidth);
    dispatch(setWidthAction(width, true));
  };

  const setSize = (size: number) => {
    const newScale = (size / maxSize) * maxScale;
    dispatch(setScaleAction(newScale));
  };

  const keydownInput = (e: KeyboardEvent, callback: () => void) => {
    if (!['Enter', 'NumpadEnter'].includes(e.code)) return;
    callback();
    (e.target as HTMLInputElement).blur();
  };

  const slider = (
    <Slider
      min={isSticker ? MIN_STICKER_SIZE : 1}
      max={maxSize}
      initialValue={Math.round(scale * state.baseSize)}
      onBlur={() => trackDesignSizing(isSticker, true)}
      onChange={setSize}
    />
  );

  const heightInput = (
    <input
      className="sticker-panel__input"
      type="text"
      value={height}
      onChange={setHeight}
      onBlur={blurHeight}
      onKeyDown={(e) => keydownInput(e, blurHeight)}
    />
  );

  const widthInput = (
    <input
      className="sticker-panel__input"
      type="text"
      value={width}
      onChange={setWidth}
      onBlur={blurWidth}
      onKeyDown={(e) => keydownInput(e, blurWidth)}
    />
  );

  return {
    slider,
    heightInput,
    widthInput,
  };
};
